import { type ReactNode, useEffect, useRef } from 'react'
import { useEnvironment } from '../shell/environment.ts'

/**
 * The one shell every confirmation in this console is built on — docs/ux/PLATFORM-ADMIN.md
 * §19 and §21.
 *
 * - **A real modal.** `showModal()` is what makes the page behind inert and moves focus in,
 *   natively, rather than a trap this application writes and gets subtly wrong. It is guarded
 *   (`typeof … === 'function'`, `!open`) because React's development double-invocation of an
 *   effect, and test environments with no `HTMLDialogElement.showModal`, would otherwise throw.
 * - **Escape is refused, always** — including the second one. §19: every confirmation here is a
 *   danger confirm, and a stray Escape must not be the difference between acting and not. Chrome
 *   does not let a page prevent the second Escape in a row (it fires `cancel` as non-cancelable and
 *   closes the dialog), so the native `close` event re-opens the dialog while React still holds it
 *   open. There is deliberately no "dismissible" mode: a dialog that is *not* a danger confirm
 *   would have to add one and say why in the same change.
 * - **Focus goes back to what opened it.** `showModal()` moves focus in; a dialog that React
 *   *removes* rather than `close()`s does not give it back, so the opener is remembered when
 *   the dialog first renders and refocused when it leaves — but only if it is still on the
 *   page. Some openers are not (a successful mint replaces the whole page with the hand-off),
 *   and forcing focus onto a detached node is a silent failure, not a recovery.
 *   Nor is it taken back from a page that has moved focus elsewhere on purpose by then (the
 *   Settings form, handing a timezone validation error back to the field that carries it).
 *
 * The opener is read from `document.activeElement` at first render, which is the control
 * that was just pressed. A browser that does not focus a button on click (Safari) leaves it
 * on `<body>`; then there is nothing to return to and none is invented.
 */
export function ModalDialog({
  labelledBy,
  children,
}: {
  readonly labelledBy: string
  readonly children: ReactNode
}) {
  const dialog = useRef<HTMLDialogElement | null>(null)
  // Captured during the first render, before the dialog's own content can take focus. A ref
  // initialised lazily so React's double render does not capture the dialog itself.
  const opener = useRef<Element | null | undefined>(undefined)
  if (opener.current === undefined) {
    opener.current = document.activeElement
  }

  useEffect(() => {
    const element = dialog.current
    if (element === null) {
      return undefined
    }
    if (typeof element.showModal === 'function' && !element.open) {
      element.showModal()
    }
    const refuseEscape = (event: Event) => {
      event.preventDefault()
    }
    element.addEventListener('cancel', refuseEscape)
    // Chrome fires `cancel` as a non-cancelable event on a **second** Escape when the first was
    // prevented, and then closes the dialog anyway. React still holds this dialog open, so a
    // dialog the DOM has closed is a dead confirmation: on screen nowhere, and in the tree still.
    // Open it again. (A dialog React has removed is not connected, and is left alone.)
    const reopen = () => {
      if (element.isConnected && !element.open && typeof element.showModal === 'function') {
        element.showModal()
      }
    }
    element.addEventListener('close', reopen)

    return () => {
      element.removeEventListener('cancel', refuseEscape)
      element.removeEventListener('close', reopen)
      // Deferred, because React runs this cleanup for a real removal and for its development
      // re-run alike. After a microtask the two differ: a removed dialog is out of the
      // document, a re-run one is still in it.
      queueMicrotask(() => {
        const target = opener.current
        // Only while focus is still lost (a removed dialog leaves it on `<body>`). A page that has
        // already put focus somewhere on purpose — the Settings form moving it to the field a
        // validation failure marked — is not overruled by the habit of going back to the opener.
        const lost = document.activeElement === null || document.activeElement === document.body
        if (lost && !element.isConnected && target instanceof HTMLElement && target.isConnected) {
          target.focus()
        }
      })
    }
  }, [])

  return (
    <dialog ref={dialog} className="confirm" aria-labelledby={labelledBy}>
      {children}
    </dialog>
  )
}

/**
 * The line above a confirmation's title: what kind of act this is, then the estate it is
 * about to happen in — both as words (§21: nothing is conveyed by colour alone), so an owner
 * with two tabs open reads *which estate* in the one place their eye lands first (§5.1).
 */
export function Kicker({ lead }: { readonly lead: string }) {
  const environment = useEnvironment()

  return (
    <p className="confirm-kicker">
      {lead} · {environment.label}
    </p>
  )
}

/** The §5.1 line every destructive confirmation shows while the estate is unreadable. */
export function UnknownEnvironmentNotice() {
  const environment = useEnvironment()

  if (environment.known) {
    return null
  }
  return (
    <p className="notice notice-failure">
      This console could not read which deployment it is pointed at. Be sure which estate this is
      before continuing.
    </p>
  )
}
